import {
  useAtlanIntegration,
  useBiIntegration,
  useCodeRepositoryIntegration,
  useDataCatalogIntegration,
  useIncidentIoIntegration,
  useRootlyIntegration,
  useMicrosoftTeamsIntegration,
  usePagerDutyIntegration,
  useTicketingIntegration,
  useOpsgenieIntegration,
  useServiceNowIntegration,
  useEnvironmentsSetup,
  useSlackIntegration,
  useTeamsBotIntegration,
} from 'shared/hooks/integrations';
import { AlertIntegrationType } from '@elementary/common-ui/services/gql/types';

type Props = {
  envId?: string;
  onUpdateSuccess?: () => void;
};

const useEnvIntegrations = ({ onUpdateSuccess, envId }: Props) => {
  const {
    environmentMutation,
    envScheduleMutation,
    deleteEnvironmentMutation,
  } = useEnvironmentsSetup({
    envId,
    onUpdateSuccess,
  });

  const {
    slackQuery,
    slackMutation,
    slackIntegrationMutation,
    isSlackConnected,
    slackChannelsMutation,
  } = useSlackIntegration({ envId, onUpdateSuccess });

  const {
    codeProjectsQuery,
    codeProjectsMutation,
    existingCodeProjects,
    codeProjectType,
    isRepositoryConnected,
    isLoading: isRepositoryLoading,
  } = useCodeRepositoryIntegration({
    envId,
    onUpdateSuccess,
    fetchFullData: true,
  });

  const {
    ticketingQuery,
    ticketingMutation,
    deleteTicketingMutation,
    ticketingProjectsMutation,
    isTicketingConnected,
  } = useTicketingIntegration({ envId, onUpdateSuccess });

  const {
    microsoftTeamsWebhooksQuery,
    microsoftTeamsDefaultsQuery,
    isTeamsConnected,
    isTeamsLoading,
    microsoftTeamsWebhooksMutation,
    microsoftTeamsDefaultsMutation,
  } = useMicrosoftTeamsIntegration({ envId, onUpdateSuccess });

  const {
    biIntegrationsQuery,
    biToolMutation,
    deleteBiIntegrationMutation,
    syncNowBiIntegrationMutation,
    isBiToolConnected,
  } = useBiIntegration({ envId, onUpdateSuccess });

  const { dataCatalogMutation, dataCatalogConfigQuery, deleteCatalogMutation } =
    useDataCatalogIntegration({ envId, onUpdateSuccess });

  const { pagerDutyQuery, pagerDutyIntegrationMutation, isPagerDutyConnected } =
    usePagerDutyIntegration();

  const { opsgenieQuery, opsgenieIntegrationMutation, isOpsgenieConnected } =
    useOpsgenieIntegration({ onUpdateSuccess });

  const {
    incidentIoQuery,
    incidentIoIntegrationMutation,
    isIncidentIoConnected,
  } = useIncidentIoIntegration({ onUpdateSuccess });

  const { rootlyQuery, rootlyIntegrationMutation, isRootlyConnected } =
    useRootlyIntegration({ onUpdateSuccess });

  const { servicenowConfigQuery } = useServiceNowIntegration({ envId });
  const { atlanQuery, testMutation, saveMutation, deleteMutation } =
    useAtlanIntegration({ envId });

  const {
    isTeamsBotConnected,
    isTeamsBotLoading,
    manifestUrl: teamsBotManifestUrl,
    appId: teamsBotAppId,
    connectedWorkspaces: teamsBotConnectedWorkspaces,
  } = useTeamsBotIntegration();

  const connectedMessagingTypes: AlertIntegrationType[] = [];
  if (isSlackConnected) connectedMessagingTypes.push('SLACK');
  if (isTeamsConnected) connectedMessagingTypes.push('MICROSOFT_TEAMS');
  if (isTeamsBotConnected) connectedMessagingTypes.push('MICROSOFT_TEAMS_BOT');

  return {
    slackQuery,
    slackMutation,
    slackIntegrationMutation,
    isSlackConnected,
    slackChannelsMutation,
    microsoftTeamsWebhooksQuery,
    microsoftTeamsDefaultsQuery,
    isTeamsConnected,
    isTeamsLoading,
    microsoftTeamsWebhooksMutation,
    microsoftTeamsDefaultsMutation,
    pagerDutyQuery,
    pagerDutyIntegrationMutation,
    opsgenieQuery,
    opsgenieIntegrationMutation,
    incidentIoQuery,
    incidentIoIntegrationMutation,
    rootlyQuery,
    rootlyIntegrationMutation,
    ticketingQuery,
    ticketingMutation,
    deleteTicketingMutation,
    ticketingProjectsMutation,
    isTicketingConnected,
    servicenowConfigQuery,
    biIntegrationsQuery,
    biToolMutation,
    deleteBiIntegrationMutation,
    syncNowBiIntegrationMutation,
    isBiToolConnected,
    codeProjectsQuery,
    codeProjectsMutation,
    existingCodeProjects,
    codeProjectType,
    isRepositoryConnected,
    isRepositoryLoading,
    environmentMutation,
    envScheduleMutation,
    deleteEnvironmentMutation,
    isIncidentManagementConnected:
      isPagerDutyConnected ||
      isOpsgenieConnected ||
      isIncidentIoConnected ||
      isRootlyConnected,
    dataCatalogMutation,
    deleteCatalogMutation,
    dataCatalogConfigQuery,
    connectedMessagingTypes,
    isTeamsBotConnected,
    isTeamsBotLoading,
    teamsBotManifestUrl,
    teamsBotAppId,
    teamsBotConnectedWorkspaces,
    atlanQuery,
    atlanTestMutation: testMutation,
    atlanSaveMutation: saveMutation,
    atlanDeleteMutation: deleteMutation,
  };
};

export default useEnvIntegrations;
